import React, { ChangeEvent } from 'react'
import { TestDetailsDto, TestQuestionDto } from 'api/Clients/Types/TestDetailsDto'
import { NameDto } from 'api/Common/Types'
import { Box, FlexBox } from 'components/Box'
import {
  FormBaseColumn,
  FormBaseContainer,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
  FormBaseSectionBlock,
} from 'components/Form/Form.styles'
import { Field, Form, FormikProps, withFormik } from 'formik'
import { FormSubmitValues } from 'utils/formValidation'

import { AppropriatenessTestNavigation } from './Navigation/AppropriatenessTestNavigation'

interface AppropriatenessTestDetailsFormValues {
  id: string
  name: string | null
  category: string | null
  tickmillCompanyId: number
  version: number
  maxFailScore: number
  maxReferredScore: number
  subcategory: string | null
  sections: TestQuestionSection[]
}

interface TestQuestionSection {
  section: NameDto
  questions: TestQuestionDto[]
}

const AppropriatenessTestDetailsFormUI: React.FC<
  FormikProps<AppropriatenessTestDetailsFormValues> & OuterProps
> = (props) => {
  const { values } = props

  const handleSubmitForm = (event: ChangeEvent<HTMLFormElement>) => {
    event.preventDefault()
    props.handleSubmit()
  }

  return (
    <FormBaseContainer>
      <AppropriatenessTestNavigation />
      <Form onSubmit={handleSubmitForm}>
        <FormBaseRow>
          <FormBaseColumn>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Name</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field type='text' name='name' disabled />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Category</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field type='text' name='category' disabled />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Sub Category</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field type='text' name='subcategory' disabled />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseColumn>
          <FormBaseColumn>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Tickmill Company</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field type='text' name='tickmillCompanyId' disabled />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Version</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field type='text' name='version' disabled />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Max Fail Score</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field type='text' name='maxFailScore' disabled />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
            <FormBaseFieldBlock>
              <FormBaseRow>
                <FormBaseFieldLabel>Max Referred Score</FormBaseFieldLabel>
                <FormBaseFieldGroup>
                  <Field type='text' name='maxReferredScore' disabled />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseColumn>
        </FormBaseRow>

        {/*TODO: temporary is hidden (backend in progress) */}
        {/*<FormBaseButtonActionWrapper>*/}
        {/*    <FlexBox justifyContent="flex-end">*/}
        {/*        <ActionButtons disabled={false} isEditMode={isEdit} setEditMode={setEdit} />*/}
        {/*    </FlexBox>*/}
        {/*</FormBaseButtonActionWrapper>*/}

        {values.sections.map((questionSection) => (
          <div key={questionSection.section.id}>
            <FormBaseRow>
              <FormBaseSectionBlock>
                <FormBaseFieldLabel>{questionSection.section.name}</FormBaseFieldLabel>
              </FormBaseSectionBlock>
            </FormBaseRow>
            <FormBaseRow>
              <FormBaseColumn>
                {questionSection.questions.map((testQuestion) => (
                  <FormBaseFieldBlock>
                    <h4>{testQuestion.question.name}</h4>
                    <QuestionWidgetFactory
                      key={testQuestion.question.id}
                      data={testQuestion}
                      widgetType={testQuestion.question.widgetType}
                    />
                  </FormBaseFieldBlock>
                ))}
              </FormBaseColumn>
            </FormBaseRow>
          </div>
        ))}
      </Form>
    </FormBaseContainer>
  )
}

enum WidgetType {
  text = 'text',
  short_text = 'short_text',
  select = 'select',
  checkbox = 'checkbox',
  radio = 'radio',
}

const QuestionWidgetFactory = ({
  data,
  widgetType,
}: {
  data: TestQuestionDto
  widgetType?: string | null
}) => {
  if (widgetType === WidgetType.text) {
    return (
      <Box width='100%'>
        <Field type='text' value='' />
      </Box>
    )
  }
  if (widgetType === WidgetType.short_text) {
    return (
      <Box width='100%'>
        <Field type='text' value='' />
      </Box>
    )
  }
  if (widgetType === WidgetType.select) {
    return (
      <Box width='100%'>
        <Field component='select' type='select'>
          {data.answers.map((x) => (
            <option key={x.id} value={x.order}>
              {x.name}
            </option>
          ))}
        </Field>
      </Box>
    )
  }
  if (widgetType === WidgetType.checkbox) {
    return (
      <Box width='100%'>
        <Field type='checkbox' checked={false} />
      </Box>
    )
  }
  if (widgetType === WidgetType.radio) {
    return (
      <Box width='100%'>
        <FlexBox textAlign='center' width='120px'>
          {data.answers.map((x) => (
            <FlexBox key={x.id} width='50%'>
              <Box width='50%' textAlign='left'>
                <Field type='radio' />
              </Box>
              <Box width='50%' textAlign='left'>
                <span>{x.name}</span>
              </Box>
            </FlexBox>
          ))}
        </FlexBox>
      </Box>
    )
  }
  return (
    <Box width='100%'>
      <Field component='select' type='select'>
        {data.answers.map((x) => (
          <option key={x.id} value={x.order}>
            {x.name}
          </option>
        ))}
      </Field>
    </Box>
  )
}

interface OuterProps {
  data: TestDetailsDto
  onSubmit(values: FormSubmitValues<TestDetailsDto>): Promise<void>
}

const dataToValues = (data: TestDetailsDto): AppropriatenessTestDetailsFormValues => {
  const questionSections = Object.entries<TestQuestionSection>(
    data?.questions.reduce(
      (previousValue: { [sectionId: string]: TestQuestionSection }, currentValue) => {
        const sectionId = currentValue.question.section.id
        const currentSection = currentValue.question.section || {}
        const previousQuestions = previousValue[sectionId]?.questions || []

        return {
          ...previousValue,
          [sectionId]: {
            section: currentSection,
            questions: previousQuestions.concat(currentValue),
          },
        }
      },
      {}
    )
  ).map(([, value]) => value)

  return {
    ...data,
    sections: questionSections,
  }
}

const dataToSubmit = (values: AppropriatenessTestDetailsFormValues): TestDetailsDto => {
  const testQuestions = values.sections.reduce(
    (previousValue: TestQuestionDto[], currentValue: TestQuestionSection) => {
      return previousValue.concat(currentValue.questions)
    },
    []
  )

  return {
    ...values,
    questions: testQuestions,
  }
}

export const AppropriatenessTestDetailsForm = withFormik<
  OuterProps,
  AppropriatenessTestDetailsFormValues
>({
  mapPropsToValues: ({ data: testDetails }) => {
    return dataToValues(testDetails)
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
      setSubmitting(false)
    } catch (e) {
      console.error('Cannot save appropriateness test details.', e)
      setSubmitting(false)
    }
  },
  enableReinitialize: true,
})(AppropriatenessTestDetailsFormUI)
